import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Send, CheckCircle } from "lucide-react";

const reviewSchema = z.object({
  name: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters")
    .regex(/^[a-zA-Z\s.'&-]+$/, "Name contains invalid characters"),
  location: z.string()
    .trim()
    .min(2, "Location must be at least 2 characters")
    .max(100, "Location must be less than 100 characters"),
  rating: z.number()
    .min(1, "Rating is required")
    .max(5, "Rating must be 5 or less"),
  text: z.string()
    .trim()
    .min(20, "Review must be at least 20 characters")
    .max(500, "Review must be less than 500 characters"),
  highlight: z.string()
    .trim()
    .max(30, "Highlight must be less than 30 characters")
    .optional(),
});

type ReviewFormData = z.infer<typeof reviewSchema>;

const ReviewSubmissionForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number>(5);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      rating: 5,
    },
  });

  const onSubmit = async (data: ReviewFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("reviews").insert({
        name: data.name,
        location: data.location,
        rating: data.rating,
        text: data.text,
        highlight: data.highlight || null,
        is_active: false, // Pending approval
        display_order: 999, // Will be reordered by admin
      });

      if (error) throw error;

      setIsSuccess(true);
      reset();
      setSelectedRating(5);
      
      // Reset success state after delay
      setTimeout(() => {
        setIsSuccess(false);
        setIsOpen(false);
      }, 3000);
    } catch (error: any) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingChange = (value: string) => {
    const rating = parseInt(value);
    setSelectedRating(rating);
    setValue("rating", rating);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setIsSuccess(false);
      reset();
      setSelectedRating(5);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Send className="w-4 h-4" />
          Share Your Story
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Your Credit Success Story</DialogTitle>
          <DialogDescription>
            Tell us how MyCreditScrub helped you improve your credit. Your review will be published after approval.
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Thank You!</h3>
            <p className="text-muted-foreground">
              Your review has been submitted and is pending approval. We appreciate your feedback!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  placeholder="Marcus T."
                  {...register("name")}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  placeholder="Houston, TX"
                  {...register("location")}
                  disabled={isSubmitting}
                />
                {errors.location && (
                  <p className="text-xs text-destructive">{errors.location.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rating">Rating *</Label>
                <Select
                  value={selectedRating.toString()}
                  onValueChange={handleRatingChange}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        <div className="flex items-center gap-1">
                          {[...Array(num)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-accent text-accent" />
                          ))}
                          <span className="ml-1">{num} Star{num !== 1 ? "s" : ""}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.rating && (
                  <p className="text-xs text-destructive">{errors.rating.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="highlight">Result Highlight</Label>
                <Input
                  id="highlight"
                  placeholder="+52 points"
                  {...register("highlight")}
                  disabled={isSubmitting}
                />
                {errors.highlight && (
                  <p className="text-xs text-destructive">{errors.highlight.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="text">Your Review *</Label>
              <Textarea
                id="text"
                placeholder="Tell us about your experience with MyCreditScrub..."
                rows={4}
                {...register("text")}
                disabled={isSubmitting}
              />
              {errors.text && (
                <p className="text-xs text-destructive">{errors.text.message}</p>
              )}
              <p className="text-xs text-muted-foreground">Min 20 characters, max 500</p>
            </div>

            <p className="text-xs text-muted-foreground">
              By submitting, you agree that your review may be displayed on our website.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Review"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewSubmissionForm;
